import type { ContactsClearResult, HistoryResetResult } from '@schwarz/contract';

import { apiSlice } from './api-slice.js';

export const maintenanceApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Clear call history so QA can re-run a scenario against the same numbers.
     *
     * Available only where the API reports `testTools` on `/auth/me`; the route
     * answers 404 otherwise.
     *
     * The invalidation list is every tag the reset can empty. `Contact` and
     * `Interaction` are the obvious ones; `Queue` and `Scheduled` are here
     * because the contacts page reads all four together and a stale queue beside
     * a cleared history is exactly the confusing half-state this is meant to
     * avoid; `Notification` because the lead alerts are gone and the bell would
     * otherwise keep showing them. The dashboard needs no tag of its own — it
     * already provides `Contact`, `Interaction`, `Queue` and `Agent`.
     */
    resetHistory: build.mutation<HistoryResetResult, void>({
      query: () => ({ url: '/maintenance/reset-history', method: 'POST' }),
      invalidatesTags: ['Contact', 'Interaction', 'Queue', 'Scheduled', 'Notification'],
    }),

    /**
     * Delete every contact, so an import can be tested from an empty table.
     *
     * Same invalidation as the reset, and for a stronger reason: contacts
     * cascade, so their queue entries, scheduled calls, calls and interactions
     * all disappear with them. Every list in the app is downstream of this one.
     */
    clearContacts: build.mutation<ContactsClearResult, void>({
      query: () => ({ url: '/maintenance/clear-contacts', method: 'POST' }),
      invalidatesTags: ['Contact', 'Interaction', 'Queue', 'Scheduled', 'Notification'],
    }),
  }),
});

export const { useResetHistoryMutation, useClearContactsMutation } = maintenanceApi;
